import 'angular-legacy-sortablejs-maintained';
export default angular.module('eventix.common.services.sortable', ['ng-sortable'])
/**
 * @ngdoc service
 * @kind function
 * @name eventix.common.services.sortByPredecessor
 * @description
 * Sort an array based on singly linked list algorithm
 *
 * @param {BaseModel[]} list The items to be sorted,
 * @param {String} [key] An items attribute containing the reference to its predecessor
 */
    .factory('sortByPredecessor', function() {
        return function SortByPredecessor(list, key = 'pivot.preceded_by') {
            if(!list.length)
                return list;

            // if no sorting method found, return list
            if(!(list[0].pivot && list[0].pivot.numerator) && !(list[0].pivot && list[0].pivot.preceded_by)){
                return list;
            }
            // If sortable by numerator / denominator
            else if(list[0].pivot && list[0].pivot.numerator){
                return _.orderBy(list, [function(item){
                    return  (1 * item.pivot.numerator) / (1 * item.pivot.denominator);
                }],['asc']);
            }
            // Else old method of preceded by
            let root = _.find(list, [key, null]);
            if(!root)
                return list;
            reposition(list, root, 0);

            let predecessor = root;
            let pk = root.constructor.primaryKey;
            let element = _.find(list, [key, predecessor[pk] ]);
            while(!_.isUndefined(element)) {
                reposition(list, element, predecessor);
                predecessor = element;
                element = _.find(list, [key, predecessor[pk] ]);
            }

            return list;
        };

        function reposition(list, el, dest) {
            let destination = dest;
            if(!_.isNumber(destination))
                destination = _.findIndex(list, destination) + 1;
            let current = _.findIndex(list, el);
            list.splice(current, 1);
            list.splice(destination, 0, el);
        }
    })
/**
 * @ngdoc service
 * @name eventix.common.services.sortableCallbacks
 * @description
 * Generators for the various callbacks related to sorting by predecessor
 * @example
 * A fullsize example of how to implement sortable functionality spans multiple files
 *
 * - A parent model that has a hasMany/belongsToMany relationship
 * - A simpleCRUD instance that manages said relationship
 * - An `ng-sortable` to provide the UI
 *
 * `common/models/Shop.js`
 * ```
 * Shop.belongsToMany('Ticket', 'tickets', '/shop/:guid/tickets', [
 *   { type: 'query' },
 *   { type: 'attach' },
 *   { type: 'detach' },
 *   { type: 'reorder', url: '/shop/:guid/tickets/reorder' },
 * ])
 * ```
 *
 * `dashboard/shops/shops.js#ShopController`
 * ```
 * vm.crud = simpleCRUD(shops, Shop.new);
 * vm.crud.addBelongsToMany('tickets',  'Ticket', {
 *     attach: sortableCallbacks.onAttach(() => vm.crud.model.tickets),
 *     detach: sortableCallbacks.onDetach(() => vm.crud.model.tickets)
 * });
 * vm.sortableConfig = {
 *     animation: 150,
 *     onSort: sortableCallbacks.onSort(function(pre, suc) {
 *       return vm.crud.model.$reorderTicket(pre,suc);
 *     })
 * }
 * ```
 *
 * `dashboard/shops/shops.html`
 * ```
 * <ul ng-sortable="vm.sortableConfig">
 *   <li ng-repeat="ticket in vm.tickets">
 *     {{ ticket.name }}
 *     <a ng-click="vm.crud.detachTicket(ticket)">&times;</a>
 *   </li>
 * </ul>
 * <select ng-options="ticket.name for ticket in vm.tickets" ng-model="toAttach"></select>
 * <button ng-click="vm.crud.attachTicket(toAttach)">Add</button>
 * ```
 *
 */
    .factory('sortableCallbacks', function($log, sortByPredecessor, $q) {
        return {
        /**
         * @ngdoc method
         * @name eventix.common.services.sortableCallbacks#onAttach
         * @methodOf eventix.common.services.sortableCallbacks
         * @description
         * Generate an onAttach callback for `simpleCRUD/belongsToMany/attach`.
         * The callback will fix the internal ordering state of the list
         * @param {Function} listGetter A function that will return the current list
         * of attached models, receives child model as argument
         * @return {Object} The attached model
         */
            onAttach: function(listGetter) {
                return function(attached) {
                    var list = listGetter(attached);
                    var pred = null;
                    if(list.length > 1) {
                        var pk = _.get(attached, 'constructor.primaryKey');
                        pred = _.nth(list, list.length - 2)[pk];
                    }
                    _.set(attached, 'pivot.preceded_by', pred);
                    return attached;
                };
            },
            /**
         * @ngdoc method
         * @name eventix.common.services.sortableCallbacks#onDetach
         * @methodOf eventix.common.services.sortableCallbacks
         * @description
         * Generate an onDetach callback for `simpleCRUD/belongsToMany/detach`.
         * The callback will fix the internal ordering state of the list
         * @param {Function} listGetter A function that will return the current list
         * of attached models, receives child model as argument
         * @return {Object} The detached model
         */
            onDetach: function(listGetter) {
                return function(detached) {
                    var pred = _.get(detached, 'pivot.preceded_by');
                    var pk = _.get(detached, 'constructor.primaryKey');
                    var succ = _.find(listGetter(detached), ['pivot.preceded_by', detached[pk]]);
                    if(succ)
                        _.set(succ, 'pivot.preceded_by', pred);
                    return detached;
                };
            },
            /**
         * @ngdoc method
         * @name eventix.common.services.sortableCallbacks#onSort
         * @methodOf eventix.common.services.sortableCallbacks
         * @description
         * Generate an onSort callback for `ng-sortable/onSort`
         * The callback works as an adapter for `BelongsToMany#reorder`. It determines who
         * is predecessor and successor, filtering out edge cases.
         * @param {Function} reorderFn The function that executes the HTTP reorder call, will be called with
         * `reorder(predecessor, successor)`. Make sure that it returns a promise
         * @return {Function} The generated callback
         */
            onSort: function(reorderFn) {
            /**
             * @param {Object} sortEvent The onSort event generated by ng-sortable
             * ```
             * {
             *   model: 'the model that was dragged',
             *   models: 'an updated array',
             *   oldIndex: 'previous index of model in models array',
             *   newIndex: 'current index of model in models array'
             * }
             * ```
             * @returns {Promise} Resolves when server has reorderd the list
             */
                return function(sortEvent) {
                    var firstStep = $q.resolve(),
                        predecessor = sortEvent.model;
                    if(sortEvent.oldIndex === sortEvent.newIndex)
                        return firstStep;
                    var newSuccessor = sortEvent.models[sortEvent.newIndex + 1];
                    if(newSuccessor === undefined) {
                        var last = _.nth(sortEvent.models, sortEvent.models.length - 2);
                        // Edge case for reordering: We can't immediately place an item at the bottom of
                        // the list the HTTP call requires a successor.
                        if(sortEvent.models.length === 2) {
                        // Only two items on the list, so dragging A-to-bottom is equiv to B-to-top
                        // Simple switch them around before making the call and avoid the edge case
                            newSuccessor = predecessor;
                            predecessor = last;
                        } else {
                        // Place the target at the one-before-last position first
                        // and then switch them around in the next call
                            firstStep = reorderFn(predecessor, last).then(() => {
                                let oldSuccessor = _.find(sortEvent.models, {pivot: { preceded_by: predecessor.guid }});
                                if(oldSuccessor)
                                    oldSuccessor.pivot.preceded_by = predecessor.pivot.preceded_by;
                                predecessor.pivot.preceded_by = last.pivot.preceded_by;
                                last.pivot.preceded_by = predecessor.guid;

                                newSuccessor = predecessor;
                                predecessor = last;
                                return $q.resolve();
                            });
                        }
                    }
                    return firstStep.then(() => {
                        return reorderFn(predecessor, newSuccessor);
                    }).then(() => {
                        let oldSuccessor = _.find(sortEvent.models, {pivot: { preceded_by: predecessor.guid }});
                        if(oldSuccessor)
                            oldSuccessor.pivot.preceded_by = predecessor.pivot.preceded_by;
                        predecessor.pivot.preceded_by = newSuccessor.pivot.preceded_by;
                        newSuccessor.pivot.preceded_by = predecessor.guid;
                        return sortByPredecessor(sortEvent.models);
                    }, error => {
                        // revert to previous sorting
                        sortByPredecessor(sortEvent.models);
                        return $q.reject(error);
                    });
                };
            }
        };
    })
    .filter('sortByPredecessor', function(sortByPredecessor) {
        return function(list, key) {
            return sortByPredecessor(list, key);
        };
    }).name;
